// Типизированные вызовы /api/public/*.
import { apiGet, apiPatch, apiPost } from './api';
import type { LoginResponse } from './auth';
import { FALLBACK_PLANS, FALLBACK_ADDONS, type PublicPlan, type PublicAddon } from '@/data/plans';

export interface CabinetData {
  profile: {
    id: string;
    name: string | null;
    email: string | null;
    phone: string | null;
    createdAt: string;
    lastLoginAt: string | null;
  };
  establishment: {
    id: string;
    name: string;
    phone: string | null;
    address: string | null;
    domain: string | null;
    timezone: string;
    currency: string;
    createdAt: string;
  } | null;
  subscription: {
    status: 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED';
    plan: string;
    billingPeriod: 'MONTHLY' | 'YEARLY';
    amount: number;
    currency: string;
    trialEndsAt: string | null;
    currentPeriodStart: string;
    currentPeriodEnd: string | null;
    cancelledAt: string | null;
    daysLeft: number | null;
    scheduledChange: {
      planCode: string;
      planName: string;
      billingPeriod: 'MONTHLY' | 'YEARLY';
      additional: number;
      applyAt: string;
    } | null;
    planDetails: {
      code: string;
      name: string;
      tagline: string | null;
      priceMonth: number;
      priceYear: number;
      features: string[];
      highlights: string[];
      maxEstablishments: number | null;
      maxCashiers: number | null;
      maxUsers: number | null;
    } | null;
    /** Подключённые модули: их цена входит в amount и в оплату (× 12 за год). */
    addons?: Array<{ code: string; name: string; priceMonthKopecks: number; quantity: number }>;
    addonsMonthKopecks?: number;
    payments: Array<{
      id: string;
      amount: number;
      currency: string;
      status: string;
      method: string | null;
      createdAt: string;
      paidAt: string | null;
    }>;
  } | null;
  stats: {
    devices: number;
    dishes: number;
    tables: number;
    orders30d: number;
  };
}

// ─── Регистрация ────────────────────────────────────────────────────────
export const signupStart = (body: {
  name: string;
  email: string;
  phone: string;
  // Город и адрес заведения — обязательны (п.20 плана 29-ispravleniya-2026-09).
  city: string;
  address: string;
  agree: true;
  marketing?: boolean;
  ref?: string;
}) => apiPost<{ sentAt: string; nextAt: string }>('/signup/start', body, { auth: false });

export const signupVerify = (body: { email: string; code: string }) =>
  apiPost<LoginResponse>('/signup/verify', body, { auth: false });

export const signupResend = (body: { email: string }) =>
  apiPost<{ sentAt: string; nextAt: string }>('/signup/resend', body, { auth: false });

// ─── Логин ─────────────────────────────────────────────────────────────
export const loginRequest = (email: string) =>
  apiPost<{ sentAt: string; nextAt: string }>('/auth/request', { email }, { auth: false });

export const loginVerify = (email: string, code: string) =>
  apiPost<LoginResponse>('/auth/verify', { email, code }, { auth: false });

// ─── ЛК ────────────────────────────────────────────────────────────────
export const getMe = () => apiGet<CabinetData>('/me');

export const updateProfile = (body: { name?: string; phone?: string }) =>
  apiPatch<CabinetData>('/me', body);

export const requestEmailChange = (email: string) =>
  apiPost<{ sentAt: string; nextAt: string }>('/me/email/request', { email });

export const verifyEmailChange = (email: string, code: string) =>
  apiPost<CabinetData>('/me/email/verify', { email, code });

export const updateEstablishment = (body: {
  name?: string;
  address?: string | null;
  phone?: string | null;
}) => apiPatch<CabinetData>('/me/establishment', body);

export const changeSubscriptionPlan = (body: {
  planCode: string;
  billingPeriod: 'MONTHLY' | 'YEARLY';
}) => apiPost<CabinetData>('/me/subscription', body);

// ─── Каталог тарифов ────────────────────────────────────────────────────
// Цены и лимиты берём из API, а витринные тексты (tagline, description,
// highlights) для известных тарифов из `data/plans.ts`: копирайт лендинга
// живёт в лендинге и не зависит от того, что лежит в каталоге бэкенда.
function withLandingCopy(plans: PublicPlan[]): PublicPlan[] {
  return plans.map((plan) => {
    const local = FALLBACK_PLANS.find((p) => p.code === plan.code);
    if (!local) return plan;
    return {
      ...plan,
      tagline: local.tagline ?? plan.tagline,
      description: local.description ?? plan.description,
      highlights: local.highlights.length > 0 ? local.highlights : plan.highlights,
    };
  });
}

export const getPublicPlans = () =>
  apiGet<{ plans: PublicPlan[] }>('/plans', { auth: false }).then((res) => ({
    ...res,
    plans: Array.isArray(res.plans) ? withLandingCopy(res.plans) : res.plans,
  }));

// ─── Каталог модулей (add-ons) ────────────────────────────────────────────
// Та же схема: цена из API, название и описание для известных модулей из лендинга.
function withLandingAddonCopy(addons: PublicAddon[]): PublicAddon[] {
  return addons.map((addon) => {
    const local = FALLBACK_ADDONS.find((a) => a.code === addon.code);
    return local ? { ...addon, name: local.name, description: local.description } : addon;
  });
}

export const getPublicAddons = () =>
  apiGet<{ addons: PublicAddon[] }>('/addons', { auth: false }).then((res) => ({
    ...res,
    addons: Array.isArray(res.addons) ? withLandingAddonCopy(res.addons) : res.addons,
  }));

// ─── Карта подключённых заведений ──────────────────────────────────────
export interface MapEstablishment {
  id: string;
  name: string;
  logoUrl: string | null;
  address: string | null;
  phone: string | null;
  lat: number;
  lon: number;
}

export interface MapPoint {
  city: string;
  /** Центр метки, среднее по заведениям города. */
  lat: number;
  lon: number;
  count: number;
  establishments: MapEstablishment[];
}

export const getEstablishmentsMap = () =>
  apiGet<{ points: MapPoint[] }>('/establishments-map', { auth: false });

// ─── Заведение для страницы /v/:id (universal link People) ─────────────
// Тот же ответ, что читает приложение на экране заведения; лендингу нужна
// только шапка: имя, лого, адрес, кухни и рейтинг.
export interface AppVenue {
  id: string;
  name: string;
  logoUrl: string | null;
  address: string | null;
  phone: string | null;
  city: string | null;
  rating: { avg: number | null; count: number } | null;
  cuisines: Array<{ slug: string; name: string }>;
  covers: string[];
  description: string | null;
  acceptsOrders: boolean;
}

export const getAppVenue = (id: string, signal?: AbortSignal) =>
  apiGet<AppVenue>(`/app/establishments/${encodeURIComponent(id)}`, { auth: false, signal });

/** Картинки бэк отдаёт относительными (/uploads/…), доклеиваем origin API. */
export function mediaUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (/^https?:\/\//.test(raw)) return raw;
  if (raw.startsWith('//')) return `https:${raw}`;
  const origin = (import.meta.env.VITE_API_URL?.trim() || '').replace(/\/+$/, '');
  return `${origin}${raw.startsWith('/') ? raw : `/${raw}`}`;
}

// ─── SSO ────────────────────────────────────────────────────────────────
// Просим backend выдать одноразовый nonce, с которым админка бесшовно
// залогинит владельца без повторного ввода пароля.
export const issueAdminSsoNonce = () =>
  apiPost<{ nonce: string; expiresIn: number }>('/sso/admin', {});
